"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import { buildNoteHref } from "@/utils/noteUrl";
import { AnchoredPopover, SelfAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/tailwind/ui/responsive-dialog";
import { useIsMobile } from "@/hooks/use-is-mobile";
import {
  MoreHorizontal,
  Share,
  Clock1,
  History,
  Link,
  Globe,
  LayoutTemplate,
  Eye,
  ChevronRight,
  Check,
  Maximize2,
  Type,
  Copy,
  Trash2,
  Lock,
  Files,
  Download,
  Upload,
} from "lucide-react";
import { getEditorActions } from "@/services-frontend/editor/editorActionsRegistry";
import { DropdownMenu, DropdownMenuFooter } from "@/components/tailwind/ui/dropdown-menu";
import { DropdownMenuItem } from "@/components/tailwind/ui/dropdown-menu/DropdownMenuItem";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";
import { toast } from "@/lib/toast";
import PublishModal from "./publishModal";
import CustomLayoutQuickPanel from "./CustomLayoutQuickPanel";
import CustomLayoutEditor from "@/components/tailwind/ui/CustomLayoutEditor";
import ImportModal from "../../import/ImportModal";
import ExportOptionsModal from "../../export/ExportOptionsModal";
import { PropertyInputSheet } from "../../board/properties/inputs/PropertyInputSheet";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useAuth } from "@/hooks/use-auth";
import { useGlobalBlocks } from "@/contexts/blockContext";
import {
  updatePageSettingsMutation,
  updateDatasourcePageSettingsMutation,
  addPrivatePageMutation,
  addPublicPageMutation,
  addWorkAreaPageMutation,
  appDB,
} from "@/indexedDB";
import { useDatasourcePages } from "@/hooks/useDatasourcePages";
import { useComputedNotes } from "@/hooks/use-computedNotes";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { toBlockRecord } from "@/indexedDB/stores/blocksStore";
import type { PageSettings, Block, IPage } from "@/types/block";
import { useRouter } from "next/navigation";
import useNoteActions from "@/hooks/use-updateNode";
import { instantiateTemplateBlocks, type TemplateParentType } from "@/services-frontend/template/templateServices";

/** Visual-only toggle for menu rows. Row's onClick does the flip — an
 *  interactive switch would double-toggle by also firing on click. */
function MenuToggleIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-flex h-[19px] w-[34px] flex-shrink-0 items-center rounded-full p-[2.5px] transition-colors ${
        checked ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-[14px] w-[14px] rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[15px]" : "translate-x-0"
        }`}
      />
    </span>
  );
}

/** Three-button font-family picker at the top of the menu (Default / Serif /
 *  Mono). Each tile previews "Ag" in the corresponding font. */
function FontPicker({
  value,
  onChange,
  disabled,
}: {
  value: "default" | "serif" | "mono";
  onChange: (next: "default" | "serif" | "mono") => void;
  disabled?: boolean;
}) {
  const options: Array<{
    value: "default" | "serif" | "mono";
    label: string;
    previewClass: string;
  }> = [
    { value: "default", label: "Default", previewClass: "font-sans" },
    { value: "serif", label: "Serif", previewClass: "font-serif" },
    { value: "mono", label: "Mono", previewClass: "font-mono" },
  ];
  return (
    <div className="flex items-stretch gap-1.5 px-2 pt-2.5 pb-2">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={`flex flex-col items-center justify-center flex-1 rounded-[7px] px-2 py-1.5 transition-colors ${
              selected
                ? "bg-selected"
                : "hover-component "
            } ${disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
            aria-pressed={selected}
          >
            <span
              className={`text-xl leading-none ${opt.previewClass} ${
                selected ? "text-blue-500" : "text-foreground"
              }`}
            >
              Ag
            </span>
            <span className="text-[11px] text-sec mt-1">
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
import {
  useHomeSections,
  HOME_SECTION_IDS,
  HOME_SECTION_LABELS,
} from "@/home/hooks/use-home-sections";
import { useNoteContext } from "@/contexts/NoteContext";
import { useBoard } from "@/contexts/boardContext";
import { updateDataSourceInDB } from "@/indexedDB";

/**
 * Props for mounting the menu. The caller supplies the document `noteId` (the
 * top-bar passes the route id, the peek passes its row id) plus optional flag
 * and action overrides. With no action overrides the menu resolves the
 * editor's handlers by id through the editor-actions registry (see
 * editorActionsRegistry); `canShare` / `isPublish` are derived from the block
 * when not passed explicitly.
 */
export interface EditorOptionsMenuProps {
  /** The document this menu acts on. */
  noteId?: string;
  canShare?: boolean;
  isPublish?: boolean;

  // Action overrides — called instead of the registry lookup by id.
  onShare?: () => void;
  onHistory?: () => void;
  onActivity?: () => void;
  onCopyContents?: () => void;

  /**
   * Override for the "Custom layout" menu item. When provided, the menu calls
   * this instead of opening the built-in `CustomLayoutQuickPanel`. The right
   * sidebar uses this to open the bigger `CustomLayoutEditor` instead — it's
   * the appropriate layout UI for database rows.
   */
  onCustomLayout?: () => void;

  /** When true, the "Custom layout" item is disabled (e.g. datasource locked). */
  disableCustomLayout?: boolean;

  /** Called after Move-to-Trash succeeds. Defaults to `router.push("/notes/home")`. */
  onAfterDelete?: () => void;

  /** When true, the Duplicate menu adds a SECOND item: "Duplicate to private". */
  showDuplicateToPrivate?: boolean;
  /**
   * Called after a successful duplicate. `target` is `"same"` (current parent —
   * the default) or `"private"` (workspace root, private section).
   * Defaults to `router.push("/notes/${newId}")`.
   */
  onAfterDuplicate?: (newId: string, target: "same" | "private") => void;
}

// ── Home settings panel ───────────────────────────────────────────────────────

function HomeOptionsMenu({ onClose }: { onClose: () => void }) {
  const { isVisible, toggleSection } = useHomeSections();
  const [showWidgets, setShowWidgets] = useState(false);

  return (
    <div className="py-1" style={{ width: 200 }}>
      {/* Show/hide widgets row */}
      <button
        type="button"
        className={`flex items-center justify-between w-full px-3 py-1.5 text-sm rounded-md transition-colors focus:outline-none ${
          showWidgets
            ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
            : "text-gray-700 dark:text-gray-300 hover-component "
        }`}
        onClick={() => setShowWidgets((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <span>Show/hide widgets</span>
        </div>
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${showWidgets ? "rotate-90" : ""}`}
        />
      </button>
      {showWidgets && (
        <SelfAnchoredPopover
          placement="left-start"
          offsetPx={6}
          onClose={() => setShowWidgets(false)}
          className="bg-modal border border-component rounded-lg shadow-2xl py-1"
          style={{ width: 190 }}
        >
          {HOME_SECTION_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className="flex items-center justify-between w-full px-3 py-1.5 text-sm rounded-md transition-colors text-gray-700 dark:text-gray-300 hover-component  focus:outline-none"
              onClick={() => toggleSection(id)}
            >
              <span>{HOME_SECTION_LABELS[id]}</span>
              {isVisible(id) && (
                <Check className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
              )}
            </button>
          ))}
        </SelfAnchoredPopover>
      )}
    </div>
  );
}

// ── Board (datasource row) custom-layout modal ────────────────────────────────

/**
 * Opens the full datasource-wide CustomLayoutEditor for a database/row page,
 * mirroring what the peek panel does. Self-contained so the board hooks only
 * run while the modal is mounted (i.e. for datasource rows). Layout is always
 * saved on the datasource — every page in it reads `datasource.pageSettings`.
 */
function BoardCustomLayoutModal({
  note,
  datasourceId,
  onClose,
}: {
  note: Block;
  datasourceId: string;
  onClose: () => void;
}) {
  const { getDataSource, getNotesByDataSourceId, updateDataSource, refreshNotes } = useBoard();
  const { currentWorkspace } = useWorkspaceContext();

  // Hydrate the datasource (schema + sibling rows) into context — needed for a
  // deep link / full-view load where the board wasn't opened first.
  useDatasourcePages(datasourceId);

  const dataSource = getDataSource(datasourceId);
  const boardProperties = dataSource?.properties || {};
  // create-property sends boardId as the API blockId (server-validated), so it
  // must be a real collection_view block — the datasource's main view.
  const boardBlockId = dataSource?.mainView ? String(dataSource.mainView) : datasourceId;

  // Datasource-level pageSettings is the source of truth; fall back to block's.
  const initialSettings = useMemo(() => {
    const dsPS = dataSource?.pageSettings;
    if (dsPS && Object.keys(dsPS).length > 0) return dsPS;
    return ((note.value as any)?.pageSettings as Record<string, any>) || {};
  }, [dataSource?.pageSettings, note.value]);

  // Compute formula / rollup values on the fly, like peek.
  const computedNotes = useComputedNotes(
    [note],
    boardProperties,
    datasourceId,
    getNotesByDataSourceId,
    getDataSource,
    refreshNotes,
  );
  const computedNote = computedNotes[0] || note;

  // Wait until the datasource schema has hydrated before opening the editor.
  if (!dataSource || Object.keys(boardProperties).length === 0) return null;

  return (
    <CustomLayoutEditor
      pageId={note._id}
      pageName={(note.value as any)?.title || "New Page"}
      databaseTitle={dataSource.title}
      boardId={boardBlockId}
      dataSourceId={datasourceId}
      workspaceId={currentWorkspace?._id || note.workspaceId}
      initialSettings={initialSettings}
      boardProperties={boardProperties}
      pagePropertyValues={(computedNote.value as any)?.databaseProperties || {}}
      previewPages={getNotesByDataSourceId(datasourceId) ?? []}
      propertyOrder={Object.keys(boardProperties)}
      onSaveLayout={async (_scope, payload) => {
        // Optimistic in-memory merge so sidebar/peek/editor re-render now.
        const latest = getDataSource(datasourceId) ?? dataSource;
        const prev = latest.pageSettings ?? {};
        updateDataSource(datasourceId, {
          pageSettings: { ...prev, ...payload.pageSettings },
        });
        await updateDatasourcePageSettingsMutation({
          dataSourceId: datasourceId,
          blockId: boardBlockId,
          workspaceId: currentWorkspace?._id || note.workspaceId || "",
          settings: payload.pageSettings,
        });
      }}
      onClose={onClose}
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EditorOptionsMenu(props: EditorOptionsMenuProps = {}) {
  const {
    noteId: noteIdProp,
    canShare: canShareProp,
    isPublish: isPublishProp,
    onShare,
    onHistory,
    onActivity,
    onCopyContents,
    onCustomLayout,
    disableCustomLayout = false,
    onAfterDelete,
    showDuplicateToPrivate = false,
    onAfterDuplicate,
  } = props;

  const pathname = usePathname();
  const isHomePage = pathname === "/notes/home";

  // Identity: the caller always supplies the id — the top-bar passes the route
  // id, the peek passes its row id. Action handlers are reached by this id
  // through the editor-actions registry, so there's no global event to race.
  const noteId = noteIdProp ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showCustomLayout, setShowCustomLayout] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const publishAnchorRef = useRef<HTMLSpanElement>(null);
  const customLayoutAnchorRef = useRef<HTMLSpanElement>(null);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const isOnline = useOnlineStatus();
  // On phones the kebab menu opens as bottom sheet instead
  // of a floating popover — easier to reach with the thumb, and the popover's
  // anchor position is awkward in the header's tight right corner.
  const isMobile = useIsMobile();

  // Full width + Small text are surfaced as top-level toggles. Per-page
  // setting, persisted via updatePageSettingsMutation (IDB + API).
  const { getBlock, updateBlock, addBlock } = useGlobalBlocks();
  const { user } = useAuth();
  const { deleteNote } = useNoteContext();
  const { ConvertNoteToTemplate } = useNoteActions();
  // Board context — used for the "Duplicate (same database)" branch where the
  // new row needs to be appended to the parent dataSource's `blockIds`.
  const { dataSources, updateDataSource } = useBoard();
  const pageBlock = noteId ? getBlock(noteId) : null;
  const pageSettings: PageSettings = ((pageBlock?.value as any)?.pageSettings ?? {}) as PageSettings;
  // A database/row page lives inside a collection (datasource). For those, the
  // "Custom layout" item must open the full board CustomLayoutEditor (datasource-
  // wide layout) instead of the per-page CustomLayoutQuickPanel — otherwise a
  // board page opened in full view shows the generic notes layout settings.
  const datasourceRowId =
    pageBlock && pageBlock.parentType === "collection" && pageBlock.parentId
      ? String(pageBlock.parentId)
      : null;
  const fullWidth = pageSettings.fullWidth ?? true;
  const smallText = pageSettings.smallText ?? false;
  const font = (pageSettings.font ?? "default") as "default" | "serif" | "mono";
  const lockPage = !!pageSettings.lockPage;

  // Derive sharing state from the block + current user so a caller can pass
  // only `noteId` (e.g. the top-bar kebab) and still get correct flags without
  // the editor broadcasting them — this mirrors how advanced-editor computes
  // `canShare` (see its readOnly / isPublicNote logic). Explicit props
  // (peek / sidebar) still win.
  const pageValue = pageBlock?.value as IPage | undefined;
  const isOwner = !!user?.email && user.email === pageValue?.userEmail;
  const blockPageType = pageValue?.pageType as string | undefined;
  // Already a template? Hide the "Create template" action so we don't offer to
  // convert a page that's already in the Templates section.
  const alreadyTemplate = pageValue?.isTemplate === true || blockPageType === "template";
  const isInTrash = pageBlock?.status === "deleted";
  const isPublicSharePath = pathname?.startsWith("/public/note/") ?? false;
  const derivedReadOnly = (blockPageType === "restricted" && !isOwner) || lockPage || isInTrash || isPublicSharePath;
  const derivedIsPublicNote = blockPageType === "public" || blockPageType === "restricted";
  const derivedCanShare = !derivedReadOnly && !derivedIsPublicNote;
  const canShare = canShareProp ?? derivedCanShare;
  const isPublish = isPublishProp ?? false;
  // Non-owners on a read-only page can't run edit-intent actions (publish toggles,
  // page-setting toggles, import, trash). Owners keep everything so they can
  // unlock / manage the page even when they themselves locked it.
  const editsDisabled = derivedReadOnly && !isOwner;

  const router = useRouter();

  const togglePageSetting = (partial: Partial<PageSettings>) => {
    if (!pageBlock || !noteId) return;
    const merged: PageSettings = { ...pageSettings, ...partial };
    updateBlock(noteId, {
      value: { ...(pageBlock.value as any), pageSettings: merged } as any,
    });
    void updatePageSettingsMutation({
      blockId: noteId,
      workspaceId: pageBlock.workspaceId,
      settings: merged,
    }).catch((err) => {
      console.error("Failed to update page settings", err);
      toast.error("Failed to update layout");
    });
  };

  // External openers (e.g. PublishToWebBanner's "Site settings") trigger the
  // same modal as the "..." menu's "Publish to web" by dispatching this event.
  // The banner is rendered for the current route note, so `noteId` already
  // points at the right page — just open the modal.
  useEffect(() => {
    const onOpenPublish = () => setShowPublish(true);
    window.addEventListener("open-publish-modal", onOpenPublish);
    return () => window.removeEventListener("open-publish-modal", onOpenPublish);
  }, [noteId]);

  // ── Note options (non-home pages) ─────────────────────────────────────────
  const noteItems: DropdownMenuItemProps[] = [];

  if (!isHomePage) {
    if (canShare) {
      noteItems.push({
        id: "share",
        label: "Share",
        icon: <Share className="w-4 h-4" />,
        onClick: () => {
          setIsOpen(false);
          if (onShare) onShare();
          else getEditorActions(noteId)?.share();
        },
      });
    }

    noteItems.push({
      id: "copy-link",
      label: "Copy Link",
      icon: <Link className="w-4 h-4" />,
      onClick: async () => {
        setIsOpen(false);
        // When a noteId is known, build a link to that specific note rather
        // than the current window URL — the peek/sidebar shows a note whose
        // URL differs from the page hosting it.
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const url = noteId
          ? `${origin}${buildNoteHref(noteId, (pageBlock?.value as any)?.title, pageBlock?.blockType)}`
          : (typeof window !== "undefined" ? window.location.href : "");
        if (url) {
          try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard");
          } catch {
            toast.error("Failed to copy link");
          }
        }
      },
    });

    // Copy page contents — markdown extracted by the editor, which registers
    // a `copyContents` handler by id (see editorActionsRegistry).
    noteItems.push({
      id: "copy-contents",
      label: "Copy page contents",
      icon: <Copy className="w-4 h-4" />,
      onClick: () => {
        setIsOpen(false);
        if (onCopyContents) onCopyContents();
        else getEditorActions(noteId)?.copyContents();
      },
    });

    if (noteId && pageBlock) {
      // Shared duplicate helper. `target` controls where the new block lands:
      //   - "same"    → under the current page's parent (workspace section /
      //                 workarea / parent page / parent dataSource). Default
      //                 behaviour for the main editor.
      //   - "private" → workspace root, private section. Used by the right
      //                 sidebar's "Duplicate to private" option to lift a
      //                 database row out of its dataSource.
      const runDuplicate = async (target: "same" | "private") => {
        const workspaceId = pageBlock.workspaceId;
        if (!workspaceId) {
          toast.error("Workspace not found");
          return;
        }

        // Where to attach the new block + which sidebar section it belongs to.
        const sourceParentId = String(pageBlock.parentId ?? workspaceId);
        const sourceParentType = String(pageBlock.parentType ?? "workspace");
        const pageType = ((pageBlock.value as any)?.pageType ?? "private") as string;

        let targetParentId: string;
        let targetParentType: string;
        let targetType: "private" | "public" | "restricted" | "workarea";

        if (target === "private") {
          // Lift to workspace-root private section.
          targetParentId = workspaceId;
          targetParentType = "workspace";
          targetType = "private";
        } else {
          // Same parent as the original.
          targetParentId = sourceParentId;
          targetParentType = sourceParentType;
          if (sourceParentType === "workarea" || pageBlock.workareaId) targetType = "workarea";
          else if (pageType === "public") targetType = "public";
          else if (pageType === "restricted") targetType = "restricted";
          else targetType = "private";
        }

        const toastId = toast.loading("Duplicating page…");

        try {
          const response: any = await instantiateTemplateBlocks({
            templateBlockId: noteId,
            targetParentId,
            workspaceId,
            targetType,
            ...(targetParentType === "workarea"
              ? { workareaId: targetParentId }
              : pageBlock.workareaId
                ? { workareaId: pageBlock.workareaId }
                : {}),
            // Pass the immediate parent's table so the server attaches the
            // cloned root to the right entity. Without this, nested-page
            // duplicates fall back to the workspace.private bucket on the
            // server and never appear inside the parent page.
            targetParentType: targetParentType as TemplateParentType,
          });

          if (!response?.newBlock) {
            toast.error("Failed to duplicate page", { id: toastId });
            return;
          }

          const newBlock = response.newBlock as Block;
          const newId = (response.newBlockId ?? newBlock._id) as string;

          // 1. Context — make the new block visible immediately.
          addBlock(newBlock);

          // 2. IDB — mirror the server's new block locally so useLiveBlock
          //    subscribers (sidebar tree, breadcrumb) re-render now rather
          //    than waiting for the next sync.
          await appDB.blocks.put(toBlockRecord(newBlock, 1));

          // 3. Placement mirror. The API already wired parent → child on
          //    the server; we just reflect it client-side. All sidebar
          //    mutations use `skipSync: true`.
          if (targetParentType === "workspace") {
            if (targetType === "private") {
              await addPrivatePageMutation(newId, workspaceId, undefined, true);
            } else if (targetType === "public" || targetType === "restricted") {
              await addPublicPageMutation(newId, workspaceId, undefined, true);
            }
          } else if (targetParentType === "workarea") {
            await addWorkAreaPageMutation(targetParentId, newId, workspaceId, undefined, true);
          } else if (targetParentType === "page") {
            // Child page → append to parent's blockIds in context + IDB.
            const parentInContext = getBlock(targetParentId);
            if (parentInContext) {
              const existing = parentInContext.blockIds ?? [];
              if (!existing.includes(newId)) {
                updateBlock(targetParentId, { blockIds: [...existing, newId] });
              }
            }
            const parentRecord = await appDB.blocks.get(targetParentId);
            if (parentRecord) {
              const existingIds = parentRecord.blockIds ?? [];
              if (!existingIds.includes(newId)) {
                await appDB.blocks.put({
                  ...parentRecord,
                  blockIds: [...existingIds, newId],
                  isSync: 0 as 0,
                  localUpdatedAt: Date.now(),
                });
              }
            }
          } else if (targetParentType === "collection") {
            // Database row → append to the parent dataSource's blockIds in
            // BOTH the board context (instant re-render of the board view)
            // AND IDB. blockIds is server-owned, so this is a local-only write
            // (updateDataSourceInDB) — it is never sent to /api/database/update.
            const ds = dataSources[targetParentId];
            if (ds) {
              const existing = ds.blockIds ?? [];
              if (!existing.includes(newId)) {
                const nextBlockIds = [...existing, newId];
                updateDataSource(targetParentId, { blockIds: nextBlockIds });
                void updateDataSourceInDB(targetParentId, { blockIds: nextBlockIds }).catch(
                  (err) =>
                    console.error("[Duplicate] dataSource blockIds IDB write failed:", err),
                );
              }
            }
          }

          toast.success("Page duplicated", { id: toastId });
          // After-action: prop wins, else default navigation.
          if (onAfterDuplicate) onAfterDuplicate(newId, target);
          else router.push(buildNoteHref(newId, (getBlock(newId)?.value as any)?.title, getBlock(newId)?.blockType));
        } catch (err) {
          console.error("Failed to duplicate page", err);
          toast.error("Failed to duplicate page", { id: toastId });
        }
      };

      // Single "Duplicate" item — default for the main editor.
      noteItems.push({
        id: "duplicate",
        label: "Duplicate",
        icon: <Files className="w-4 h-4" />,
        onClick: async () => {
          setIsOpen(false);
          await runDuplicate("same");
        },
      });

      // Optional second item — used by the right sidebar to offer "lift to
      // workspace private" alongside "duplicate in same parent".
      if (showDuplicateToPrivate) {
        noteItems.push({
          id: "duplicate-to-private",
          label: "Duplicate to private",
          icon: <Files className="w-4 h-4" />,
          onClick: async () => {
            setIsOpen(false);
            await runDuplicate("private");
          },
        });
      }

      // Convert the current page into a reusable template (in place). The whole
      // move — re-rooting to the workspace, detaching from the old parent/section
      // (so it doesn't linger in its previous spot), flipping the template flag,
      // and registering it in the Templates sidebar list — lives in
      // useNoteActions.ConvertNoteToTemplate (via NoteContext), mirroring moveNote.
      //
      // Owner-only, and only for ordinary pages: skip ones already templated,
      // database rows (a collection child isn't a standalone template), and the
      // database/collection pages themselves (blockType !== "page").
      if (isOwner && !alreadyTemplate && !datasourceRowId && pageBlock.blockType === "page") {
        noteItems.push({
          id: "create-template",
          label: "Create template",
          icon: <LayoutTemplate className="w-4 h-4" />,
          onClick: async () => {
            setIsOpen(false);
            await ConvertNoteToTemplate(noteId);
          },
        });
      }
    }

    noteItems.push({
      id: "publish",
      label: "Publish to web",
      disabled: !isOnline || !isOwner,
      icon: <Globe className="w-4 h-4" />,
      onClick: () => {
        if (!isOnline || !isOwner) return;
        setIsOpen(false);
        setShowPublish(true);
      },
    });

    if (!isPublish && noteId) {
      // Full width: desktop only — mobile is already edge-to-edge.
      if (!isMobile) {
        noteItems.push({
          id: "full-width",
          label: "Full width",
          disabled: editsDisabled,
          icon: <Maximize2 className="w-4 h-4" />,
          onClick: () => {
            if (editsDisabled) return;
            togglePageSetting({ fullWidth: !fullWidth });
          },
          rightElement: <MenuToggleIndicator checked={fullWidth} />,
        });
      }

      noteItems.push({
        id: "small-text",
        label: "Small text",
        disabled: editsDisabled,
        icon: <Type className="w-4 h-4" />,
        onClick: () => {
          if (editsDisabled) return;
          togglePageSetting({ smallText: !smallText });
        },
        rightElement: <MenuToggleIndicator checked={smallText} />,
      });

      noteItems.push({
        id: "custom-layout",
        label: "Custom layout",
        subtitle: "Arrange properties and body",
        icon: <LayoutTemplate className="w-4 h-4" />,
        disabled: disableCustomLayout || editsDisabled,
        onClick: () => {
          if (disableCustomLayout || editsDisabled) return;
          setIsOpen(false);
          // Callback wins (sidebar uses this to open CustomLayoutEditor —
          // the right UI for database rows). Default behaviour opens the
          // built-in CustomLayoutQuickPanel, used by the main editor.
          if (onCustomLayout) onCustomLayout();
          else setShowCustomLayout(true);
        },
      });

      // Lock page — owner-only; toggles read-only via pageSettings.lockPage.
      // The editor reads this and folds it into `effectiveReadOnly`.
      if (isOwner) {
        noteItems.push({
          id: "lock-page",
          label: "Lock page",
          icon: <Lock className="w-4 h-4" />,
          onClick: () => togglePageSetting({ lockPage: !lockPage }),
          rightElement: <MenuToggleIndicator checked={lockPage} />,
        });
      }
    }

    if (!isPublish) {
      noteItems.push(
        {
          id: "activity",
          label: "Updates & analytics",
          subtitle: "Views, edits and visitors",
          disabled: !isOnline,
          icon: <Clock1 className="w-4 h-4" />,
          onClick: () => {
            if (!isOnline) return;
            setIsOpen(false);
            if (onActivity) onActivity();
            else getEditorActions(noteId)?.activity();
          },
        },
        {
          id: "history",
          label: "View History",
          disabled: !isOnline,
          icon: <History className="w-4 h-4" />,
          onClick: () => {
            if (!isOnline) return;
            setIsOpen(false);
            if (onHistory) onHistory();
            else getEditorActions(noteId)?.history();
          },
        },
      );

      // Import — opens the existing ImportModal with the current page as the
      // parent. Imported content lands as children of this page.
      if (noteId) {
        noteItems.push({
          id: "import",
          label: "Import",
          disabled: editsDisabled,
          icon: <Download className="w-4 h-4" />,
          onClick: () => {
            if (editsDisabled) return;
            setIsOpen(false);
            setShowImport(true);
          },
        });
      }

      // Export — opens ExportOptionsModal for the current page (markdown /
      // HTML / zip via the existing exportImportService).
      if (noteId && pageBlock) {
        noteItems.push({
          id: "export",
          label: "Export",
          icon: <Upload className="w-4 h-4" />,
          onClick: () => {
            setIsOpen(false);
            setShowExport(true);
          },
        });
      }

      // Move to Trash — delegates to useNoteActions.DeleteNote (via
      // useNoteContext) which already handles sidebar reorder removal
      // (root pages), parent blockIds update (child pages), trash mutation,
      // recent-notes cleanup, and toast.
      // After-action: prop wins, else default to navigating home (sidebar
      // callers typically pass onClose so the sidebar closes in place).
      if (noteId) {
        noteItems.push({
          id: "move-to-trash",
          label: "Move to Trash",
          disabled: !isOwner,
          icon: <Trash2 className="w-4 h-4" />,
          variant: "destructive",
          onClick: () => {
            if (!isOwner) return;
            setIsOpen(false);
            // Fire the trash mutation. deleteNote in NoteContext is fire-
            // and-forget so we don't await it.
            deleteNote(noteId);
            if (onAfterDelete) onAfterDelete();
            else router.push("/notes/home");
          },
        });
      }
    }
  }

  if (!isHomePage && noteItems.length === 0) return null;

  // Shared button + content so we can conditionally wrap it in either a
  // Popover (desktop) or ResponsiveDialog bottom sheet (mobile) without
  // duplicating the menu definition.
  const triggerButton = (
    <button title="Style, export and more..." className="flex items-center gap-2 hover-component p-1 rounded-md transition  group">
      <MoreHorizontal className="w-[18px] h-[18px] text-xs transition-colors group-hover:bg-muted dark:group-hover:bg-[rgb(42,42,42)]" />
    </button>
  );
  // Guided: file-level and destructive actions live in a tinted footer, which
  // reads as a different place before the labels are even read.
  const footerIds = new Set(["import", "export", "move-to-trash"]);
  const bodyItems = noteItems.filter((it) => !footerIds.has(String(it.id)));
  const footerItems = noteItems.filter((it) => footerIds.has(String(it.id)));
  // On mobile we want a thin horizontal divider between every item so the
  // bottom-sheet menu reads as a list of distinct rows (iOS mobile
  // pattern). `DropdownMenu` already exposes `dividerAfter` — we just pass
  // every index except the last one (no trailing divider). Desktop keeps
  // the existing tight popover style with no dividers.
  const mobileDividerAfter = bodyItems.map((_, i) => i).slice(0, -1);
  // Desktop dividers — group the menu into logical sections (sharing /
  // page settings / meta / destructive). Indices computed by item id so
  // adding/removing items above doesn't break divider positions.
  // Group boundaries for desktop. "duplicate-to-private" (when shown) stays
  // in the same group as the other copy/duplicate items by NOT being a divider
  // anchor — the divider falls AFTER "publish" so the whole Copy/Duplicate
  // cluster is one block.
  const desktopDividerIds = ["publish", "lock-page", "history"];
  const desktopDividerAfter = desktopDividerIds
    .map((id) => bodyItems.findIndex((it) => it.id === id))
    .filter((i) => i >= 0);
  // Font picker sits at the very top of the non-home menu, above the divider
  // and the item list — matches the screenshot the user shared.
  const fontPickerNode = !isHomePage && !isPublish && noteId ? (
    <>
      <FontPicker
        value={font}
        disabled={editsDisabled}
        onChange={(next) => togglePageSetting({ font: next })}
      />
    </>
  ) : null;
  const menuBody = isHomePage ? (
    <HomeOptionsMenu onClose={() => setIsOpen(false)} />
  ) : (
    <>
      {fontPickerNode}
      <DropdownMenu items={bodyItems} dividerAfter={desktopDividerAfter} />
      {footerItems.length > 0 && <DropdownMenuFooter items={footerItems} />}
    </>
  );

  return (
    <>
      {isMobile ? (
        // Mobile: bottom sheet. Easier thumb reach; menu items get more
        // breathing room than a cramped popover anchored in the header's
        // top-right corner.
        <ResponsiveDialog open={isOpen} onOpenChange={setIsOpen}>
          <ResponsiveDialogTrigger asChild>{triggerButton}</ResponsiveDialogTrigger>
          <ResponsiveDialogContent forceLayout="sheet" className="max-w-md">
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>{isHomePage ? "Home options" : "Actions"}</ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            {/* Larger item padding (`[&_button]:py-3`) + dividers between
                rows so the menu reads as a list of taps on touch devices.
                The `&_button` arbitrary selector lets us hit every nested
                DropdownMenuItem button without modifying DropdownMenuItem
                itself (so the same component stays compact on desktop). */}
            <div className="mt-2 [&_button]:py-3 [&_button]:rounded-none">
              {isHomePage ? (
                <HomeOptionsMenu onClose={() => setIsOpen(false)} />
              ) : (
                <>
                  {fontPickerNode}
                  <DropdownMenu items={bodyItems} dividerAfter={mobileDividerAfter} />
                  {footerItems.length > 0 && <DropdownMenuFooter items={footerItems} />}
                </>
              )}
            </div>
          </ResponsiveDialogContent>
        </ResponsiveDialog>
      ) : (
        <>
          <button
            ref={menuTriggerRef}
            title="Style, export and more..."
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 hover-component p-1 rounded-md transition  group"
          >
            <MoreHorizontal className="w-[18px] h-[18px] text-xs transition-colors group-hover:bg-muted dark:group-hover:bg-[rgb(42,42,42)]" />
          </button>
          {isOpen && (
            <AnchoredPopover
              anchor={menuTriggerRef}
              onClose={() => setIsOpen(false)}
              placement="bottom-end"
              offsetPx={8}
              className="w-72 rounded-[10px] border border-component bg-modal p-0 text-popover-foreground shadow-2xl outline-none overflow-hidden"
              style={{ width: isHomePage ? "200px" : "312px", minWidth: isHomePage ? "200px" : "312px" }}
            >
              {menuBody}
            </AnchoredPopover>
          )}
        </>
      )}

      {/* Publish-to-web: portal'd bottom sheet on mobile, Radix Popover
          anchored to the (invisible) trigger on desktop. PublishModal has its
          own header with an X, so hide PropertyInputSheet's chrome on mobile. */}
      {noteId && (
        isMobile ? (
          <PropertyInputSheet
            open={showPublish}
            onClose={() => setShowPublish(false)}
            title="Publish to web"
            desktopAnchorClassName=""
            hideHeader
          >
            <PublishModal
              open={showPublish}
              onClose={() => setShowPublish(false)}
              noteId={noteId}
            />
          </PropertyInputSheet>
        ) : (
          <>
            <span ref={publishAnchorRef} className="sr-only" aria-hidden />
            {showPublish && (
              <AnchoredPopover
                anchor={publishAnchorRef}
                onClose={() => setShowPublish(false)}
                placement="bottom-end"
                offsetPx={8}
                className="w-auto rounded-lg border-0 bg-transparent p-0 text-popover-foreground shadow-none outline-none"
              >
                <div
                  style={{ display: "contents" }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && !e.defaultPrevented) {
                      e.stopPropagation();
                      setShowPublish(false);
                    }
                  }}
                >
                  <PublishModal
                    open={showPublish}
                    onClose={() => setShowPublish(false)}
                    noteId={noteId}
                  />
                </div>
              </AnchoredPopover>
            )}
          </>
        )
      )}

      {/* Customize layout. A database/row page (parent is a collection) opens
          the full datasource-wide CustomLayoutEditor — the same one the peek
          uses — so a board page shows its own layout settings even in full
          view. Ordinary pages open the per-page CustomLayoutQuickPanel:
          portal'd bottom sheet on mobile, anchored Popover on desktop. */}
      {noteId && showCustomLayout && datasourceRowId && pageBlock && (
        <BoardCustomLayoutModal
          note={pageBlock}
          datasourceId={datasourceRowId}
          onClose={() => setShowCustomLayout(false)}
        />
      )}
      {noteId && !datasourceRowId && (
        isMobile ? (
          <PropertyInputSheet
            open={showCustomLayout}
            onClose={() => setShowCustomLayout(false)}
            title="Customize layout"
            desktopAnchorClassName=""
          >
            <CustomLayoutQuickPanel noteId={noteId} />
          </PropertyInputSheet>
        ) : (
          <>
            <span ref={customLayoutAnchorRef} className="sr-only" aria-hidden />
            {showCustomLayout && (
              <AnchoredPopover
                anchor={customLayoutAnchorRef}
                onClose={() => setShowCustomLayout(false)}
                placement="bottom-end"
                offsetPx={8}
                className="w-72 rounded-lg border border-component bg-modal p-0 text-popover-foreground shadow-2xl outline-none"
              >
                <CustomLayoutQuickPanel noteId={noteId} />
              </AnchoredPopover>
            )}
          </>
        )
      )}

      {/* Import modal — opens with the current page as the import target so
          imported content lands as children of this page. */}
      {showImport && noteId && pageBlock && (
        <ImportModal
          onClose={() => setShowImport(false)}
          parentId={noteId}
          parentTable="page"
          workareaId={
            String(pageBlock.parentType) === "workarea"
              ? String(pageBlock.parentId)
              : undefined
          }
        />
      )}

      {/* Export modal — exports the current page (markdown / HTML / zip). */}
      {showExport && noteId && pageBlock && (
        <ExportOptionsModal
          pageId={noteId}
          pageTitle={(pageBlock.value as any)?.title || "New page"}
          blockType={
            (pageBlock.blockType as "page" | "collection_view") ?? "page"
          }
          onClose={() => setShowExport(false)}
        />
      )}
    </>
  );
}
